import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import {
  JAMB_SUBJECTS,
  WAEC_TRACKS,
  ADULT_EDUCATION_SKILLS,
  LEARNER_LEVELS,
} from "../../data/tutorPrograms";
import "./RequestTutorModal.css";

const EXAM_TYPES = [
  "JAMB",
  "WAEC/NECO",
  "Adult Education",
  "School Subject",
  "Other",
];

const MAX_JAMB_SUBJECTS = 4; // the standard UTME subject combination size

const initialForm = {
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  subject: "",
  examType: "School Subject",
  learnerLevel: "",
  mode: "online",
  location: "",
  preferredSchedule: "",
  budgetMin: "",
  budgetMax: "",
  tutorPreference: "",
  jambSubjects: [],
  waecTrack: "",
  adultEducationSkill: "",
  learnerLevelStructured: "",
};

const RequestTutorModal = ({ onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ...initialForm,
    contactName: user
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : "",
    contactPhone: user?.phone || "",
    contactEmail: user?.email || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [matches, setMatches] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Switching the category clears the other categories' picks so a stray
  // leftover selection can't get submitted alongside the new examType.
  const handleExamTypeChange = (e) => {
    const examType = e.target.value;
    setForm((prev) => ({
      ...prev,
      examType,
      jambSubjects: [],
      waecTrack: "",
      adultEducationSkill: "",
    }));
  };

  const toggleJambSubject = (subjectName) => {
    setForm((prev) => {
      const already = prev.jambSubjects.includes(subjectName);
      if (already) {
        return {
          ...prev,
          jambSubjects: prev.jambSubjects.filter((s) => s !== subjectName),
        };
      }
      if (prev.jambSubjects.length >= MAX_JAMB_SUBJECTS) {
        toast.error(`Pick up to ${MAX_JAMB_SUBJECTS} subjects — a standard UTME combination.`);
        return prev;
      }
      return { ...prev, jambSubjects: [...prev.jambSubjects, subjectName] };
    });
  };

  // For the 3 core categories, the structured picks ARE the request — this
  // turns them into the human-readable `subject` summary the backend still
  // stores (and uses as a fallback for anything the structured match
  // doesn't cover), instead of asking the learner to type the same thing
  // twice.
  const buildSubjectSummary = () => {
    if (form.examType === "JAMB") {
      return form.jambSubjects.length
        ? `JAMB: ${form.jambSubjects.join(", ")}`
        : "";
    }
    if (form.examType === "WAEC/NECO") {
      return form.waecTrack ? `WAEC/NECO: ${form.waecTrack}` : "";
    }
    if (form.examType === "Adult Education") {
      return form.adultEducationSkill
        ? `Adult Education: ${form.adultEducationSkill}${
            form.learnerLevelStructured ? ` (${form.learnerLevelStructured})` : ""
          }`
        : "";
    }
    return form.subject;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const subject = buildSubjectSummary();

    if (!form.contactName.trim() || !form.contactPhone.trim() || !subject.trim()) {
      const whatsMissing =
        form.examType === "JAMB"
          ? "select at least one subject"
          : form.examType === "WAEC/NECO"
            ? "select a track"
            : form.examType === "Adult Education"
              ? "select a skill"
              : "fill in the subject";
      toast.error(`Please fill in your name, contact number, and ${whatsMissing}.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/tutor-requests", { ...form, subject });
      toast.success("Request submitted!");
      setMatches(res.data.matches || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const viewProfile = (tutor) => {
    const route = tutor.role === "institution" ? "/academy" : "/tutor";
    navigate(`${route}/${tutor._id}`);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="request-tutor-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          ×
        </button>

        {matches === null ? (
          <>
            <h3 className="success-title">Request a Tutor</h3>
            <p className="success-message">
              Tell us what you need and we'll suggest suitable tutors or
              training centres — our team also reviews every request for
              hands-on matching.
            </p>

            <form className="request-tutor-form" onSubmit={handleSubmit}>
              <div className="rt-form-row">
                <label>
                  Your Name *
                  <input
                    name="contactName"
                    value={form.contactName}
                    onChange={handleChange}
                    required
                  />
                </label>
                <label>
                  WhatsApp/Contact Number *
                  <input
                    name="contactPhone"
                    value={form.contactPhone}
                    onChange={handleChange}
                    required
                  />
                </label>
              </div>

              <label>
                Email (optional)
                <input
                  name="contactEmail"
                  type="email"
                  value={form.contactEmail}
                  onChange={handleChange}
                />
              </label>

              <label>
                Category *
                <select name="examType" value={form.examType} onChange={handleExamTypeChange}>
                  {EXAM_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>

              {/* JAMB — subject combination (up to 4, the standard UTME size) */}
              {form.examType === "JAMB" && (
                <div className="rt-field-group">
                  <span className="rt-field-group-label">
                    Subject Combination * (up to {MAX_JAMB_SUBJECTS})
                  </span>
                  <div className="rt-pill-options">
                    {JAMB_SUBJECTS.map((s) => (
                      <button
                        type="button"
                        key={s}
                        className={`rt-pill${form.jambSubjects.includes(s) ? " rt-pill-selected" : ""}`}
                        onClick={() => toggleJambSubject(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* WAEC/NECO — track, not individual subjects */}
              {form.examType === "WAEC/NECO" && (
                <label>
                  Track *
                  <select
                    name="waecTrack"
                    value={form.waecTrack}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a track…</option>
                    {WAEC_TRACKS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {/* Adult Education — skill + learner level */}
              {form.examType === "Adult Education" && (
                <div className="rt-form-row">
                  <label>
                    Skill *
                    <select
                      name="adultEducationSkill"
                      value={form.adultEducationSkill}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a skill…</option>
                      {ADULT_EDUCATION_SKILLS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Learner Level
                    <select
                      name="learnerLevelStructured"
                      value={form.learnerLevelStructured}
                      onChange={handleChange}
                    >
                      <option value="">Not sure</option>
                      {LEARNER_LEVELS.map((l) => (
                        <option key={l} value={l}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}

              {/* School Subject / Other — free text, as before */}
              {(form.examType === "School Subject" || form.examType === "Other") && (
                <label>
                  Learning Need / Subject *
                  <input
                    name="subject"
                    placeholder="e.g. Spoken French, Piano, Grade 10 Chemistry..."
                    value={form.subject}
                    onChange={handleChange}
                    required
                  />
                </label>
              )}

              <label>
                Learner Age (optional)
                <input
                  name="learnerLevel"
                  placeholder="e.g. 15 years, SS2"
                  value={form.learnerLevel}
                  onChange={handleChange}
                />
              </label>

              <div className="rt-form-row">
                <label>
                  Mode
                  <select name="mode" value={form.mode} onChange={handleChange}>
                    <option value="online">Online</option>
                    <option value="physical">Physical</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </label>
                <label>
                  Location
                  <input
                    name="location"
                    placeholder="e.g. Durumi, Abuja"
                    value={form.location}
                    onChange={handleChange}
                  />
                </label>
              </div>

              <label>
                Preferred Days &amp; Times
                <input
                  name="preferredSchedule"
                  placeholder="e.g. Weekday evenings, weekends"
                  value={form.preferredSchedule}
                  onChange={handleChange}
                />
              </label>

              <div className="rt-form-row">
                <label>
                  Budget Min (₦)
                  <input
                    name="budgetMin"
                    type="number"
                    min="0"
                    value={form.budgetMin}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Budget Max (₦)
                  <input
                    name="budgetMax"
                    type="number"
                    min="0"
                    value={form.budgetMax}
                    onChange={handleChange}
                  />
                </label>
              </div>

              <label>
                Tutor Preference (optional)
                <input
                  name="tutorPreference"
                  placeholder="e.g. Female tutor, native French speaker..."
                  value={form.tutorPreference}
                  onChange={handleChange}
                />
              </label>

              <button
                type="submit"
                className="success-ok-btn"
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit Request"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h3 className="success-title">
              {matches.length > 0
                ? "Here are some suitable tutors"
                : "Request received"}
            </h3>
            <p className="success-message">
              {matches.length > 0
                ? "Our team is also reviewing your request for further matching."
                : "We couldn't find an immediate match, but our team will review your request and follow up on WhatsApp."}
            </p>

            {matches.length > 0 && (
              <div className="rt-matches-list">
                {matches.map((m) => (
                  <button
                    key={m._id}
                    className="rt-match-item"
                    onClick={() => viewProfile(m)}
                  >
                    <span className="rt-match-name">
                      {m.role === "institution"
                        ? m.academyName
                        : `${m.firstName} ${m.lastName}`}
                    </span>
                    <span className="rt-match-rating">
                      {m.averageRating > 0
                        ? `⭐ ${m.averageRating.toFixed(1)}`
                        : "New"}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <button className="success-ok-btn" onClick={onClose}>
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestTutorModal;
